import React, { useState } from 'react';
import { BaseLayout } from '@/components/layout';
import { useSurveillanceDashboard } from '../hooks/useSurveillanceDashboard';
import { MediaLightbox } from './MediaLightbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Search, Download, Play } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { AlertDetail } from '../types/types';

export const SurveillanceDashboard: React.FC = () => {
  const {
    data,
    isLoading,
    error,
    searchQuery,
    dateFilter,
    resultFilter,
    currentPage,
    handleSearch,
    handleDateFilter,
    handleResultFilter,
    handlePageChange,
    clearFilters,
  } = useSurveillanceDashboard();

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertDetail | null>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  React.useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const openLightbox = (alert: AlertDetail, mediaIndex: number) => {
    setSelectedAlert(alert);
    setSelectedMediaIndex(mediaIndex);
    setLightboxOpen(true);
  };

  const downloadAllMedia = async (alert: AlertDetail) => {
    for (const media of alert.media) {
      const link = document.createElement('a');
      link.href = media.file_url;
      link.download = media.filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Add a small delay between downloads to prevent browser blocking
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchInput);
  };

  if (isLoading && !data) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-muted-foreground">Loading surveillance reports...</div>
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout>
        <div className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </BaseLayout>
    );
  }

  const hasActiveFilters = searchQuery || dateFilter || resultFilter;

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <h2 className="text-3xl font-bold text-foreground mb-8">
            Surveillance Reports for {data?.context.project.name}
          </h2>

          {/* Search and Filters */}
          <div className="mb-8">
            <form onSubmit={handleSearchSubmit}>
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Input */}
                <div className="flex-1 flex items-center bg-input border border-border rounded-lg px-4 py-3 shadow-sm">
                  <Input
                    type="text"
                    placeholder="Search alerts..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 bg-transparent"
                  />
                  <button type="submit" className="ml-2">
                    <Search className="h-5 w-5 text-muted-foreground" />
                  </button>
                </div>

                {/* Date Filter */}
                <div className="md:w-64">
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => handleDateFilter(e.target.value)}
                    className="w-full h-full shadow-sm"
                  />
                </div>

                {/* Result Filter */}
                <div className="md:w-64">
                  <Select value={resultFilter || undefined} onValueChange={handleResultFilter}>
                    <SelectTrigger className="w-full h-full shadow-sm">
                      <SelectValue placeholder="All Results" />
                    </SelectTrigger>
                    <SelectContent>
                      {data?.context.result_choices.map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    type="button"
                    onClick={clearFilters}
                    variant="default"
                    className="whitespace-nowrap"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </form>
          </div>

          {/* Alert Cards */}
          {data?.alerts && data.alerts.length > 0 ? (
            <div className="space-y-6">
              {data.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="bg-card rounded-lg shadow-md overflow-hidden border border-border"
                >
                  {/* Alert Header */}
                  <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/50">
                    <div className="flex items-center space-x-4">
                      <img
                        src="/static/images/favicon.jpg"
                        alt="Logo"
                        className="h-8 w-8 rounded"
                      />
                      <span className="text-sm text-foreground hidden md:block">
                        {alert.user_info?.email}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Created at {alert.formatted_timestamp}
                      </span>
                    </div>
                  </div>

                  {/* Subject and Tags */}
                  <div className="px-6 py-4 border-b border-border">
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {alert.subject}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">
                        {alert.result}
                      </span>
                      {alert.client_notified && alert.notified_user_info && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-accent text-accent-foreground border border-border">
                          Contacted {alert.notified_user_info.first_name}{' '}
                          {alert.notified_user_info.last_name}
                          {alert.notified_user_info.job_title &&
                            ` - ${alert.notified_user_info.job_title}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="px-6 py-4 border-b border-border">
                    <p className="text-foreground whitespace-pre-wrap">
                      {alert.description}
                    </p>
                  </div>

                  {/* Conclusion */}
                  <div className="px-6 py-4 border-b border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-2">
                      Conclusion
                    </h4>
                    <p className="text-foreground whitespace-pre-wrap">
                      {alert.conclusion}
                    </p>
                  </div>

                  {/* Media */}
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-foreground">
                        Media ({alert.media_count})
                      </h4>
                      {alert.media && alert.media.length > 0 && (
                        <button 
                          onClick={() => downloadAllMedia(alert)}
                          className="flex items-center text-sm text-primary hover:text-primary/80 transition-colors"
                        >
                          <span className="mr-2">Download all</span>
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {alert.media && alert.media.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {alert.media.slice(0, 8).map((media, index) => (
                          <button
                            key={media.id}
                            onClick={() => openLightbox(alert, index)}
                            className="relative group aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                          >
                            {media.is_video ? (
                              <>
                                <img
                                  src="/static/images/surv_small.jpg"
                                  alt="Video thumbnail"
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                  <div className="bg-card/90 rounded-full p-2">
                                    <Play className="h-6 w-6 text-foreground fill-foreground" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <img
                                src={media.file_url}
                                alt={media.filename}
                                className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                              />
                            )}
                          </button>
                        ))}
                        {alert.media_count > 8 && (
                          <button
                            onClick={() => openLightbox(alert, 8)}
                            className="aspect-square bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm font-medium hover:bg-accent transition-colors"
                          >
                            +{alert.media_count - 8} more
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No media available.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-md border border-border p-12 text-center">
              <p className="text-muted-foreground text-lg">
                No alerts available for {data?.context.project.name}
              </p>
            </div>
          )}

          {/* Pagination */}
          {data?.pagination && data.pagination.num_pages > 1 && (
            <div className="mt-8 flex justify-center items-center gap-4">
              {data.pagination.has_previous && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    &laquo; First
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                </>
              )}

              <span className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground">
                Page {data.pagination.current_page} of {data.pagination.num_pages}
              </span>

              {data.pagination.has_next && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(data.pagination.num_pages)}
                    disabled={currentPage === data.pagination.num_pages}
                  >
                    Last &raquo;
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Media Lightbox */}
      {selectedAlert && selectedAlert.media && (
        <MediaLightbox
          media={selectedAlert.media}
          initialIndex={selectedMediaIndex}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      )}
    </BaseLayout>
  );
};

